import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { db } from "@/db/db"
import { shelfTypeOrder } from "@/db/schema"
import { logAuditEvent } from "@/features/audit-logs/server/auditLogger"
import {
  shelfTypeOrderPayloadSchema,
  type ShelfTypeOrderEntry,
} from "@/features/shelf-type-order/core/shelfTypeOrderSchema"
import { loadShelfTypeOrder } from "@/features/shelf-type-order/server/shelfTypeOrderLoader"
import { assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

export const getShelfTypeOrderFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async (): Promise<ShelfTypeOrderEntry[]> => {
    const request = getRequest()
    await assertPermission(request, "shelfTypeOrder", "read")
    return loadShelfTypeOrder()
  })

export const saveShelfTypeOrderFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator((data: { entries: ShelfTypeOrderEntry[] }) => {
    const parsed = shelfTypeOrderPayloadSchema.safeParse(data.entries)
    if (!parsed.success) {
      const message = parsed.error.issues
        .map((i) => i.message)
        .filter((m, idx, arr) => arr.indexOf(m) === idx)
        .join("; ")
      throw new Error(`Invalid shelf-type order: ${message}`)
    }
    return { entries: parsed.data }
  })
  .handler(async ({ data }): Promise<ShelfTypeOrderEntry[]> => {
    const request = getRequest()
    const user = await assertPermission(request, "shelfTypeOrder", "update")

    const before = await loadShelfTypeOrder()

    await db.transaction(async (tx) => {
      await tx.delete(shelfTypeOrder)
      await tx.insert(shelfTypeOrder).values(
        data.entries.map((e, idx) => ({
          shelfType: e.shelfType,
          sortOrder: idx,
          isVisible: e.isVisible,
          lastModifiedBy: user.id,
        })),
      )
    })

    await logAuditEvent(
      {
        action: "shelfTypeOrder.update",
        entityType: "shelfTypeOrder",
        entityId: "global",
        entityName: "Shelf-type order",
        summary: `Updated shelf-type order`,
        metadata: {
          before: before.map((e) => ({ shelfType: e.shelfType, isVisible: e.isVisible })),
          after: data.entries.map((e) => ({ shelfType: e.shelfType, isVisible: e.isVisible })),
        },
      },
      request,
    )

    return data.entries.map((e, idx) => ({
      shelfType: e.shelfType,
      sortOrder: idx,
      isVisible: e.isVisible,
    }))
  })
